import { useRef, useState } from 'react';

import { Icon } from './icon';
import { Button } from './shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover';

export interface SearchableComboboxOption {
  label: string;
  value: string;
}

interface SearchableComboboxProps {
  options: SearchableComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  icon?: string;
  triggerClassName?: string;
  containerClassName?: string;
  clearable?: boolean;
}

export function SearchableCombobox({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  icon,
  triggerClassName = 'w-52 justify-between font-normal',
  containerClassName = 'flex items-center justify-end gap-2',
  clearable = true,
}: SearchableComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery('');
    setOpen(next);
  };

  const selectedLabel = options.find((o) => o.value === value)?.label;
  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  const showClear = clearable && value !== '';

  return (
    <div className={containerClassName}>
      {icon && <Icon className={`${icon} text-gray-600`} />}
      <span className="relative">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={open} className={triggerClassName}>
              {selectedLabel ? (
                <p className="truncate">{selectedLabel}</p>
              ) : (
                <p className="text-gray-500">{placeholder}</p>
              )}
              <Icon className="lucide--chevrons-up-down ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={searchPlaceholder}
                value={query}
                onValueChange={(v) => {
                  setQuery(v);
                  listRef.current?.scrollTo({ top: 0 });
                }}
              />
              <CommandList ref={listRef} className="max-h-64 overflow-y-auto">
                <CommandEmpty>{emptyText}</CommandEmpty>
                <CommandGroup>
                  {filtered.map((option) => (
                    <CommandItem
                      key={option.value}
                      className="cursor-pointer hover:bg-gray-100"
                      value={option.value}
                      onSelect={() => {
                        onChange(option.value);
                        handleOpenChange(false);
                      }}
                    >
                      <span>{option.label}</span>
                      {value === option.value && <Icon className="lucide--check ml-auto" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {showClear && (
          <Button
            variant="ghost"
            size="iconSm"
            onClick={() => onChange('')}
            className="absolute top-1/2 right-8 -translate-y-1/2"
            type="button"
          >
            <Icon className="lucide--x" />
          </Button>
        )}
      </span>
    </div>
  );
}
